<template>
  <div class="container">
    <!-- Search Box and Button -->
    <div class="dropdown-container">
      <!-- First Row -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="loadAsmList">
          <option value="">All FSMs</option>
          <option
            v-for="fsm in fsmList"
            :key="fsm.profile_id"
            :value="fsm.profile_id"
          >
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select
          id="asmSelect"
          v-model="selectedAsm"
          @change="loadDistributorList"
        >
          <option value="">All ASMs</option>
          <option
            v-for="asm in asmList"
            :key="asm.profile_id"
            :value="asm.profile_id"
          >
            {{ asm.name }}
          </option>
        </select>
      </div>

      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select
          id="distributorSelect"
          v-model="selectedDistributor"
          @change="loadDealerList"
          :disabled="disableDistributor"
        >
          <option value="">All Distributors</option>
          <option
            v-for="distributor in distributorList"
            :key="distributor.profile_id"
            :value="distributor.profile_id"
          >
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <div class="dropdown-item">
        <label for="dealerSelect">Dealer</label>
        <select id="dealerSelect" v-model="selectedDealer">
          <option value="">All Dealers</option>
          <option
            v-for="dealer in dealerList"
            :key="dealer.profile_id"
            :value="dealer.profile_id"
            :class="{ 'inactive-option': isInactiveDealer(dealer) }"
          >
            {{ dealer.name }}
          </option>
        </select>
      </div>

      <!-- New Order Number Search -->
      <div class="dropdown-item">
        <label for="orderNo">Invoice No</label>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Enter Invoice No"
          class="search-input"
          @keydown.enter="performSearch"
        />
      </div>

      <!-- Second Row -->
      <div class="dropdown-item">
        <label for="startDatePicker">Start Date</label>
        <input
          type="date"
          v-model="startDate"
          class="search-input"
          @change="handleDateChange"
          :max="currentDate"
        />
      </div>

      <div class="dropdown-item">
        <label for="endDatePicker">End Date</label>
        <input
          type="date"
          v-model="endDate"
          class="search-input"
          @change="handleDateChange"
          :max="currentDate"
        />
      </div>

      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default mt-4">
          Search
        </button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-spinner">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Conditionally Show Data or Message -->
    <div
      v-if="!loading && invoiceDetails && invoiceDetails.length"
      class="invoice-results"
    >
      <!-- Grid for Items and Counts -->
      <!-- Professional horizontal product summary -->
      <!-- Product Summary -->
      <div v-if="items.length" class="product-summary-section">
        <div class="product-summary-header">
          <div class="product-summary-title-group">
            <h6>Product Summary</h6>
          </div>

          <div class="product-scroll-actions">
            <button
              type="button"
              class="product-scroll-btn"
              :disabled="!canScrollItemsLeft"
              title="Scroll left"
              @click="scrollItems('left')"
            >
              <i class="fa fa-chevron-left"></i>
            </button>

            <button
              type="button"
              class="product-scroll-btn"
              :disabled="!canScrollItemsRight"
              title="Scroll right"
              @click="scrollItems('right')"
            >
              <i class="fa fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <div
          ref="itemsScroller"
          class="items-horizontal-scroll"
          @scroll="updateItemScrollState"
        >
          <div
            v-for="(item, index) in items"
            :key="`${item.item_name}-${index}`"
            class="product-item-card"
          >
            <div class="product-item-main">
              <span class="product-item-name" :title="item.item_name">
                {{ item.item_name }}
              </span>
            </div>

            <div class="product-item-quantity">
              <strong>{{ parseInt(item.qty) || 0 }}</strong>
              <span>Qty</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Tiles for Sales, VAT, Gross Total, Paid, and Due Payment -->
      <!-- Professional financial summary cards -->
      <!-- Financial Summary -->
      <div class="financial-summary-grid">
        <div class="financial-summary-card summary-net">
          <div class="summary-card-heading">
            <div class="summary-icon">
              <i class="fa fa-calculator"></i>
            </div>

            <span class="summary-label">Net Total</span>
          </div>

          <div class="summary-value">
            <small>Rs.</small>
            <strong>{{ formatNumber(totals.total_net) }}</strong>
          </div>
        </div>

        <div class="financial-summary-card summary-vat">
          <div class="summary-card-heading">
            <div class="summary-icon">
              <i class="fa fa-percent"></i>
            </div>

            <span class="summary-label">VAT</span>
          </div>

          <div class="summary-value">
            <small>Rs.</small>
            <strong>{{ formatNumber(totals.total_vat) }}</strong>
          </div>
        </div>

        <div class="financial-summary-card summary-gross">
          <div class="summary-card-heading">
            <div class="summary-icon">
              <i class="fa fa-file-text-o"></i>
            </div>

            <span class="summary-label">Gross Total</span>
          </div>

          <div class="summary-value">
            <small>Rs.</small>
            <strong>{{ formatNumber(totals.total_gross) }}</strong>
          </div>
        </div>

        <div class="financial-summary-card summary-paid">
          <div class="summary-card-heading">
            <div class="summary-icon">
              <i class="fa fa-check-circle"></i>
            </div>

            <span class="summary-label">Paid</span>
          </div>

          <div class="summary-value">
            <small>Rs.</small>
            <strong>{{ formatNumber(totals.total_paid) }}</strong>
          </div>
        </div>

        <div class="financial-summary-card summary-due">
          <div class="summary-card-heading">
            <div class="summary-icon">
              <i class="fa fa-exclamation-circle"></i>
            </div>

            <span class="summary-label">Due</span>
          </div>

          <div class="summary-value">
            <small>Rs.</small>
            <strong>{{ formatNumber(totals.total_due) }}</strong>
          </div>
        </div>
      </div>

      <!-- Table for Invoice Details -->
      <div class="card mt-3">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr class="border-bottom-primary">
                <th scope="col" class="invoice-number-col">
                  Invoice No & Date
                  <i
                    class="fa fa-info-circle info-icon"
                    @click.stop="toggleInfoTooltip"
                    title="Click for details"
                  ></i>
                </th>
                <th scope="col">Dealer</th>
                <th scope="col" class="text-right">Net Amount (Rs.)</th>
                <th scope="col" class="text-right">VAT Amount (Rs.)</th>
                <th scope="col" class="text-right">Gross Amount (Rs.)</th>
                <th scope="col" class="text-right">Payment (Rs.)</th>
                <th scope="col" class="text-right">Balance (Rs.)</th>
                <th scope="col">Created By</th>
                <th scope="col">Status</th>
                <th scope="col" class="action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(invoice, index) in invoiceDetails" :key="index" :class="{ 'inactive-dealer-row': String(invoice.dealer_is_active) === '0' }">
                <td
                  class="invoice-number-col"
                  :title="invoice.invoices_no || ''"
                >
                  <div class="invoice-no-wrap">
                    <div class="invoice-no-line">
                      <span class="invoice-no-text">{{ invoice.invoices_no }}</span>
                      <i
                        v-if="invoice.vat_liable == 1 || invoice.vat_liable === '1' || invoice.vat_liable === true"
                        class="fa fa-check-circle vat-verification-badge"
                        title="Tax Invoice"
                      ></i>
                    </div>
                    <div class="invoice-date-text">
                      ({{ invoice.created_at }})
                    </div>
                  </div>
                </td>
                <td>{{ invoice.dealer_name }}</td>
                <td class="text-right">{{ formatNumber(invoice.net_total) }}</td>
                <td class="text-right">{{ formatNumber(invoice.vat) }}</td>
                <td class="text-right">{{ formatNumber(invoice.gross_total) }}</td>
                <td class="text-right">{{ formatNumber(invoice.paid_amount) }}</td>
                <td class="text-right">{{ formatNumber(invoice.balance) }}</td>
                <td>{{ invoice.distributor }}</td>
                <td>
                  <span :class="statusClass(invoice.is_cancel)">
                    {{ invoice.is_cancel === "1" ? "CANCELLED" : "ACTIVE" }}
                  </span>
                </td>
                <td class="action-col">
                  <i
                    @click="showInvoiceDetails(invoice)"
                    class="fa fa-eye"
                    aria-hidden="true"
                  ></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Show Message When No Invoices Found -->
    <div
      v-if="!loading && (!invoiceDetails || !invoiceDetails.length)"
      class="no-invoices-message"
    >
      <p>No Invoices Found.</p>
    </div>

    <InvoiceModal
      :showModal="showModal"
      :invoiceNo="selectedInvoiceNo"
      @close="closeModal"
    />

    <!-- Info Tooltip Floating Overlay -->
    <teleport to="body">
      <div
        v-if="showInfoTooltip"
        class="header-info-tooltip"
        :style="{ top: tooltipTop + 'px', left: tooltipLeft + 'px' }"
        @click.stop
      >
        <div class="tooltip-arrow"></div>
        <div class="tooltip-content">
          <span>Invoices marked with <i class="fa fa-check-circle vat-verification-badge-sm"></i> icon represent Tax Invoices.</span>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDealerListManagementTrue,
  fetchInvoicesByDistributor,
} from "@/services/dataService";
import InvoiceModal from "../../Modal/invoicePopup.vue";

export default {
  components: { InvoiceModal },

  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    return {
      token: localStorage.getItem("token_set") || "",
      profileType: localStorage.getItem("profile_type") || "", // new
      profileId: localStorage.getItem("profile_id") || "", // new
      disableDistributor: false, // new

      fsmList: [],
      asmList: [],
      distributorList: [],
      dealerList: [],
      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedDealer: "",
      searchQuery: "",
      startDate: currentDate,
      endDate: currentDate,
      loading: false,
      currentDate,
      items: [],
      invoiceDetails: [],
      totals: {
        total_net: "0.00",
        total_vat: "0.00",
        total_gross: "0.00",
        total_paid: "0.00",
        total_due: "0.00",
      },
      showModal: false,
      selectedInvoiceNo: null,
      canScrollItemsLeft: false,
      canScrollItemsRight: false,
      showInfoTooltip: false,
      tooltipTop: 0,
      tooltipLeft: 0,
    };
  },

  created() {
    this.initializePage();
  },

  mounted() {
    window.addEventListener("resize", this.updateItemScrollState);
    document.addEventListener("click", this.closeInfoTooltip);
    window.addEventListener("scroll", this.closeInfoTooltip, true);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updateItemScrollState);
    document.removeEventListener("click", this.closeInfoTooltip);
    window.removeEventListener("scroll", this.closeInfoTooltip, true);
  },

  watch: {
    showModal(newVal) {
      document.body.style.overflow = newVal ? "hidden" : "";
    },
  },

  computed: {
    selectedDealerIsInactive() {
      if (!this.selectedDealer) return false;
      const d = this.dealerList.find(
        (x) => String(x.profile_id) === String(this.selectedDealer),
      );
      return this.isInactiveDealer(d);
    },
  },

  methods: {
    toggleInfoTooltip(event) {
      if (this.showInfoTooltip) {
        this.showInfoTooltip = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltipTop = rect.bottom + window.scrollY + 6;
      this.tooltipLeft = Math.max(10, rect.left + window.scrollX - 15);
      this.showInfoTooltip = true;
    },

    closeInfoTooltip(e) {
      if (this.showInfoTooltip) {
        if (e && e.target && e.target.closest && e.target.closest(".info-icon")) {
          return;
        }
        this.showInfoTooltip = false;
      }
    },
    handleDateChange() {
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate);
        const end = new Date(this.endDate);
        if (end < start) {
          this.showErrorToast("Invalid date range picked.");
          this.endDate = "";
        }
      }
    },

    showErrorToast(message) {
      const toast = useToast();
      toast.error(message, { position: "top-right", timeout: 3000 });
    },

    formatNumber(value) {
      if (!value) return "0.00";
      return parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async initializePage() {
      this.loading = true;

      try {
        this.token = localStorage.getItem("token_set") || "";

        this.profileId = localStorage.getItem("profile_id") || "";

        this.profileType = localStorage.getItem("profile_type") || "";

        if (!this.token) {
          this.showErrorToast(
            "Authentication token was not found. Please log in again.",
          );
          return;
        }

        await this.loadFsmList();
        await this.loadAsmList();
      } catch (error) {
        console.error("Error initializing invoice page:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchMerged(ids, fetchFn) {
      const responses = await Promise.all(
        ids.map((id) => fetchFn(id).catch(() => null)),
      );
      const seen = new Map();
      responses.forEach((res) => {
        (res?.data?.data || []).forEach((row) => seen.set(row.profile_id, row));
      });
      return [...seen.values()];
    },

    async loadFsmList() {
      try {
        const profileId = this.profileId;
        const response = await fetchFsmList(profileId, this.token);
        this.fsmList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching FSM list:", error);
      }
    },

    scrollItems(direction) {
      const scroller = this.$refs.itemsScroller;

      if (!scroller) {
        return;
      }

      const firstCard = scroller.querySelector(".product-item-card");

      const cardWidth = firstCard?.getBoundingClientRect().width || 220;

      const gap = 12;

      const scrollAmount = Math.max(
        cardWidth + gap,
        Math.min(scroller.clientWidth * 0.75, (cardWidth + gap) * 3),
      );

      scroller.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });

      window.setTimeout(this.updateItemScrollState, 350);
    },

    updateItemScrollState() {
      this.$nextTick(() => {
        const scroller = this.$refs.itemsScroller;

        if (!scroller) {
          this.canScrollItemsLeft = false;
          this.canScrollItemsRight = false;
          return;
        }

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

        this.canScrollItemsLeft = scroller.scrollLeft > 2;

        this.canScrollItemsRight =
          maxScrollLeft > 2 && scroller.scrollLeft < maxScrollLeft - 2;
      });
    },

    async loadAsmList() {
      try {
        const response = await fetchAsmList(
          this.profileId,
          this.selectedFsm || null,
          this.token,
        );
        this.asmList = response.data.data || [];
      } catch (error) {
        console.error("Error loading ASM list:", error);
        this.asmList = [];
      }
      this.selectedAsm = "";
      await this.loadDistributorList();
    },

    // ✅ Updated logic here
    async loadDistributorList() {
      try {
        if (this.profileType === "distributor") {
          this.disableDistributor = true;

          // Fetch all distributors (for consistency)
          const response = await fetchDistributorList(
            this.profileId, null, this.token
          );

          const allDistributors = response.data.data || [];

          // Filter logged-in distributor only
          this.distributorList = allDistributors.filter(
            (d) => d.profile_id == this.profileId,
          );

          // Auto-select and lock it
          this.selectedDistributor = this.profileId;
          await this.loadDealerList();
          return;
        }

        let list = [];
        // For non-distributors
        if (this.selectedAsm) {
          const response = await fetchDistributorList(
            this.profileId,
            this.selectedAsm,
            this.token,
          );
          list = response.data.data || [];
        } else if (this.selectedFsm) {
          list = await this.fetchMerged(
            this.asmList.map((a) => a.profile_id),
            (asmId) => fetchDistributorList(this.profileId, asmId, this.token),
          );
        } else {
          const res = await fetchDistributorList(this.profileId, null, this.token);
          list = res.data.data || [];
        }
        this.distributorList = list;
        this.selectedDistributor = "";
      } catch (error) {
        console.error("Error loading Distributor list:", error);
        this.distributorList = [];
        this.selectedDistributor = "";
      }
      await this.loadDealerList();
    },

    async loadDealerList() {
      if (this.selectedDistributor) {
        try {
          const response = await fetchDealerListManagementTrue(
            this.profileId,
            this.selectedDistributor,
            true,
            this.token,
          );
          this.dealerList = response.data.data || [];
          this.selectedDealer = "";
        } catch (error) {
          console.error("Error loading dealer list:", error);
        }
      } else {
        this.dealerList = [];
        this.selectedDealer = "";
      }
    },

    async performSearch() {
      this.loading = true;
      this.items = [];
      this.invoiceDetails = [];
      this.totals = {
        total_net: "0.00",
        total_vat: "0.00",
        total_gross: "0.00",
        total_paid: "0.00",
        total_due: "0.00",
      };

      // if (!this.selectedDistributor) {
      //   this.showErrorToast("Please select a Distributor before searching.");
      //   this.loading = false;
      //   return;
      // }

      try {
        const formattedStartDate = this.formatDateForApi(this.startDate);
        const formattedEndDate = this.formatDateForApi(this.endDate);

        const searchData = {
          fsm_id: this.selectedFsm || null,
          asm_id: this.selectedAsm || null,
          distributor_id: this.selectedDistributor || null,
          rep_id: this.selectedDealer || null,
          invoice_no: this.searchQuery || null,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        };

        const response = await fetchInvoicesByDistributor(
          searchData,
          this.token,
        );
        this.invoiceDetails = response.data.invoices;
        this.totals = response.data.totals[0];
        this.items = response.data.items;

        await this.$nextTick();
        this.updateItemScrollState();
      } catch (error) {
        console.error("Error performing search:", error);
      } finally {
        this.loading = false;
      }
    },

    formatDateForApi(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(
        -2,
      )}-${("0" + date.getDate()).slice(-2)}`;
    },

    async showInvoiceDetails(invoice) {
      this.selectedInvoiceNo = invoice.invoices_no;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    statusClass(isCancel) {
      if (isCancel === "1") return "badge badge-cancel";
      if (isCancel === "0") return "badge badge-active";
      return "badge badge-secondary";
    },

    isInactiveDealer(d) {
      return !!d && (String(d.is_active) === "0" || d.is_active === false);
    },
  },
};
</script>

<style scoped>
/* Ensure the body and html take the full height of the page */
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

/* Container for the entire section */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

/* =========================================================
   FILTERS — RESPONSIVENESS ONLY
========================================================= */

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: end;
  gap: 15px;
  width: 100%;
  min-width: 0;
  justify-content: center;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.dropdown-item {
  width: 100%;
  min-width: 0;
  max-width: none;
  box-sizing: border-box;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input[type="date"],
input[type="text"] {
  width: 100%;
  max-width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #cccccc;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.search-input {
  width: 100%;
  max-width: 100%;
  flex: 1;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #cccccc;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-default:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.btn.button-default:active {
  transform: scale(0.95);
}

/* Eye icon */
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

/* Keep redesigned sections inside the existing page width */
.invoice-results {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  box-sizing: border-box;
}

.invoice-results .product-summary-section,
.invoice-results .financial-summary-grid,
.invoice-results .card,
.invoice-results .table-responsive {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* =========================================================
   PRODUCT SUMMARY — SITE-MATCHED COMPACT DESIGN
========================================================= */

.product-summary-section {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-top: 4px;
  padding: 12px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035);
}

.product-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.product-summary-title-group {
  min-width: 0;
}

.product-summary-title-group h6 {
  margin: 0;
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.product-summary-title-group span {
  display: block;
  margin-top: 2px;
  color: #8a94a6;
  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.35;
}

.product-scroll-actions {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.product-scroll-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #e2e7ed;
  border-radius: 7px;
  background-color: #ffffff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 9px;
  transition: border-color 0.18s ease, background-color 0.18s ease,
    color 0.18s ease, transform 0.18s ease;
}

.product-scroll-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background-color: #f8fafc;
  color: #1f2937;
  transform: translateY(-1px);
}

.product-scroll-btn:active:not(:disabled) {
  transform: translateY(0);
}

.product-scroll-btn:disabled {
  border-color: #edf0f4;
  background-color: #fafbfc;
  color: #cbd1d8;
  cursor: not-allowed;
  transform: none;
}

.items-horizontal-scroll {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 1px 0 5px;
  box-sizing: border-box;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;

  /* Hide scrollbar in Firefox */
  scrollbar-width: none;

  /* Hide scrollbar in older Edge/IE */
  -ms-overflow-style: none;
}

/* Hide scrollbar in Chrome, Edge, Safari */
.items-horizontal-scroll::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.items-horizontal-scroll::-webkit-scrollbar {
  height: 2px;
}

.product-item-card {
  flex: 0 0 215px;
  min-width: 215px;
  min-height: 74px;
  padding: 11px 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 9px;
  box-sizing: border-box;
  border: 1px solid #e9edf2;
  border-radius: 9px;
  background-color: #ffffff;
  box-shadow: 0 2px 7px rgba(15, 23, 42, 0.03);
  scroll-snap-align: start;
  transition: border-color 0.18s ease, background-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.product-item-card:hover {
  border-color: #d8e0e9;
  background-color: #fcfdff;
  box-shadow: 0 5px 13px rgba(15, 23, 42, 0.055);
  transform: translateY(-1px);
}

.product-item-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.product-item-index {
  width: 23px;
  height: 23px;
  flex: 0 0 23px;
  border-radius: 6px;
  background-color: #f1f5f9;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
}

.product-item-name {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  color: #334155;
  font-size: 11px;
  font-weight: 650;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.product-item-quantity {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 5px;
}

.product-item-quantity strong {
  color: #1d4f91;
  font-size: 18px;
  font-weight: 750;
  line-height: 1;
}

.product-item-quantity span {
  color: #94a3b8;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
}

/* =========================================================
   FINANCIAL SUMMARY — SITE-MATCHED FLAT CARDS
========================================================= */

.financial-summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-top: 12px;
  box-sizing: border-box;
}

.financial-summary-card {
  --summary-color: #52667d;
  --summary-soft: #f3f6f9;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #fafbfd;

  min-width: 0;
  min-height: 96px;
  padding: 13px 14px;
  box-sizing: border-box;
  border: 1px solid #e8ecf1;
  border-radius: 10px;

  /* Extremely light gradient */
  background: linear-gradient(
    135deg,
    var(--summary-bg-start) 0%,
    var(--summary-bg-end) 100%
  );

  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.financial-summary-card:hover {
  border-color: #d8e0e9;
  box-shadow: 0 6px 15px rgba(15, 23, 42, 0.055);
  transform: translateY(-1px);
}

.summary-card-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.summary-icon {
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  border-radius: 7px;
  background-color: var(--summary-soft);
  color: var(--summary-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.summary-label {
  min-width: 0;
  color: #64748b;
  font-size: 10.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.summary-value {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  margin-top: 13px;
  white-space: nowrap;
}

.summary-value small {
  color: #94a3b8;
  font-size: 9.5px;
  font-weight: 600;
}

.summary-value strong {
  min-width: 0;
  overflow: hidden;
  color: #1f2937;
  font-size: clamp(16px, 1.2vw, 21px);
  font-weight: 750;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
}

.summary-net {
  --summary-color: #3b6ea8;
  --summary-soft: #edf4fb;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #f6faff;
}

.summary-vat {
  --summary-color: #7161a8;
  --summary-soft: #f2f0f8;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #faf8ff;
}

.summary-gross {
  --summary-color: #5b6673;
  --summary-soft: #f1f3f5;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #fafbfc;
}

.summary-paid {
  --summary-color: #37805c;
  --summary-soft: #edf6f1;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #f7fcf9;
}

.summary-due {
  --summary-color: #b64b52;
  --summary-soft: #faeff0;
  --summary-bg-start: #ffffff;
  --summary-bg-end: #fff8f9;
}

/* =========================================================
   TABLE — PREMIUM INNER SCROLLING
========================================================= */

.invoice-results .card.mt-3 {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  border-radius: 10px;
  box-sizing: border-box;
}

.table-responsive {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  max-height: 520px;
  overflow: auto;
  position: relative;
  box-sizing: border-box;
  overscroll-behavior: contain;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #aeb9c7 #f4f6f8;
}

/* Chrome, Edge and Safari */
.table-responsive::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-responsive::-webkit-scrollbar-track {
  background-color: #f4f6f8;
}

.table-responsive::-webkit-scrollbar-thumb {
  border: 1px solid transparent;
  border-radius: 999px;
  background-color: #aeb9c7;
  background-clip: padding-box;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background-color: #8997a8;
}

.table-responsive::-webkit-scrollbar-corner {
  background-color: #f4f6f8;
}

.table-responsive .table {
  width: 100%;
  min-width: 1250px;
  margin-bottom: 0;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #dddddd;
  white-space: nowrap;
  vertical-align: middle;
}

.table th.text-right,
.table td.text-right {
  text-align: right;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: #f9f9f9;
  font-weight: bold;
  box-shadow: 0 1px 0 #dce2e9;
  vertical-align: middle;
}

.table td {
  background-color: #ffffff;
}

.badge-cancel,
.badge-active,
.badge-secondary {
  display: inline-block;
  min-width: 92px;
  text-align: center;
  padding: 5px 10px;
  border-radius: 4px;
  box-sizing: border-box;
}

.badge-cancel {
  background-color: #dc3545;
  color: white;
}

.badge-active {
  background-color: #28a745;
  color: white;
}

.no-invoices-message {
  text-align: center;
  margin-top: 50px;
  font-size: 18px;
  color: #666666;
}

/* =========================================================
   RESPONSIVE FILTERS
========================================================= */

@media (max-width: 1199px) {
  .dropdown-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .financial-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 899px) {
  .dropdown-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .financial-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-item-card {
    flex-basis: 190px;
    min-width: 190px;
  }

  .table-responsive {
    max-height: 460px;
  }
}

@media (max-width: 575px) {
  .dropdown-container {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .dropdown-item,
  .search-container {
    width: 100%;
  }

  .search-container .btn.button-default {
    width: 100%;
  }

  .product-summary-section {
    padding: 11px 11px 8px;
  }

  .product-summary-header {
    align-items: center;
  }

  .product-summary-header span {
    display: none;
  }

  .product-item-card {
    flex-basis: 172px;
    min-width: 172px;
    min-height: 80px;
    padding: 10px 11px;
  }

  .product-item-icon {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }

  .financial-summary-grid {
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .financial-summary-card {
    min-height: 102px;
    padding: 13px 14px 16px;
  }

  .summary-value {
    margin-top: 10px;
  }

  .summary-value strong {
    font-size: 20px;
  }

  .table-responsive {
    max-height: 420px;
  }
}

/* Invoice Number: show complete value in single flat row */
.invoice-no-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoice-no-line {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.invoice-no-text {
  font-weight: 600;
  color: #212529;
}

.vat-verification-badge {
  color: #1a335e;
  font-size: 14px;
  margin-left: 2px;
  vertical-align: middle;
}

.invoice-date-text {
  color: #6c757d;
  font-size: 12px;
}

/* Info Icon & Teleported Popover Overlay */
.info-icon {
  color: #007bff;
  cursor: pointer;
  font-size: 13px;
  margin-left: 6px;
  vertical-align: middle;
  transition: color 0.2s, transform 0.2s;
}

.info-icon:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.header-info-tooltip {
  position: absolute;
  z-index: 999999;
  background-color: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -4px rgba(0, 0, 0, 0.05);
  max-width: 270px;
  white-space: normal;
  pointer-events: auto;
  animation: tooltipFadeIn 0.15s ease-out;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tooltip-arrow {
  position: absolute;
  top: -6px;
  left: 18px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #ffffff;
  filter: drop-shadow(0 -1px 1px rgba(0, 0, 0, 0.08));
}

.tooltip-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.vat-verification-badge-sm {
  color: #1a335e;
  font-size: 14px;
  margin: 0 2px;
  vertical-align: middle;
}

/* Action: compact column only for the eye button */
.table th.action-col,
.table td.action-col {
  width: 68px;
  min-width: 68px;
  max-width: 68px;
  padding-right: 6px;
  padding-left: 6px;
  text-align: center;
  white-space: nowrap;
}

.table td.action-col .fa-eye {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dropdown-item select option {
  background-color: #ffffff;
}

/* inactive dealers: red background only, normal text colour */
.dropdown-item select option.inactive-option {
  background-color: #ffebee;
}

/* select box when an inactive dealer is selected */
.dropdown-item select.inactive-select {
  background-color: #ffebee;
}

.table tr.inactive-dealer-row td { background-color: #ffebee; }

@media (max-width: 1199px) {
  .financial-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 899px) {
  .financial-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-item-card {
    flex-basis: 195px;
    min-width: 195px;
  }
}

@media (max-width: 575px) {
  .product-summary-section {
    padding: 10px;
  }

  .product-summary-title-group span {
    display: none;
  }

  .product-item-card {
    flex-basis: 175px;
    min-width: 175px;
    min-height: 70px;
    padding: 10px;
  }

  .financial-summary-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .financial-summary-card {
    min-height: 88px;
    padding: 12px 13px;
  }

  .summary-value {
    margin-top: 10px;
  }

  .summary-value strong {
    font-size: 19px;
  }
}
</style>